import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaChartLine, FaExchangeAlt, FaShieldAlt, FaDatabase, FaClipboardCheck, FaNetworkWired } from 'react-icons/fa';
import Section from '../common/Section';
import Card from '../common/Card';

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--space-8);
  margin-top: var(--space-12);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border-radius: var(--border-radius-xl);
  padding: var(--space-8);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: var(--gradient-primary);
    transform: scaleX(0);
    transition: transform var(--transition-normal);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-2xl);
    border-color: var(--primary-light);
    
    &:before {
      transform: scaleX(1);
    }
  }
  
  &:hover .service-icon {
    background: var(--gradient-primary);
    color: white;
    transform: scale(1.1) rotate(5deg);
  }
  
  &:hover .service-link {
    color: var(--primary-color);
    transform: translateX(4px);
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-6);
  color: var(--primary-color);
  font-size: var(--font-size-3xl);
  transition: all var(--transition-normal);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--gradient-primary);
    border-radius: var(--border-radius-lg);
    opacity: 0;
    transition: opacity var(--transition-normal);
  }
  
  svg {
    position: relative;
    z-index: 1;
  }
`;

const ServiceTitle = styled.h3`
  font-size: var(--font-size-xl);
  font-weight: 700;
  margin-bottom: var(--space-4);
  color: var(--text-dark);
  line-height: 1.3;
`;

const ServiceDescription = styled.p`
  color: var(--text-medium);
  line-height: 1.7;
  margin-bottom: var(--space-6);
  font-size: var(--font-size-base);
`;

const ServiceLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  transition: all var(--transition-normal);
  font-size: var(--font-size-sm);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  &:after {
    content: '→';
    margin-left: var(--space-2);
    transition: transform var(--transition-normal);
    font-weight: 700;
  }
  
  &:hover:after {
    transform: translateX(4px);
  }
`;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

const item = {
  hidden: { y: 30, opacity: 0 },
  show: { 
    y: 0, 
    opacity: 1, 
    transition: { 
      duration: 0.6,
      ease: "easeOut"
    } 
  }
};

const Services = () => {
  const services = [
    {
      icon: <FaChartLine />,
      title: 'US Energy Trading Risk Management',
      description: 'Comprehensive US gas and energy trading risk management with real-time position monitoring, P&L tracking, and CFTC compliance analytics for commodity trading firms.',
      link: '/services/market-risk-management'
    },
    {
      icon: <FaExchangeAlt />,
      title: 'Energy Trading Data Integration',
      description: 'Seamless integration of US energy trading data across multiple platforms, exchanges, and regulatory reporting systems for gas and power markets.',
      link: '/services/trade-data-integration'
    },
    {
      icon: <FaShieldAlt />,
      title: 'US Energy Trading Compliance',
      description: 'CFTC compliant regulatory reporting and compliance monitoring for US gas and energy trading operations with automated regulatory submissions.',
      link: '/services/compliance-solutions'
    },
    {
      icon: <FaDatabase />,
      title: 'Position Limits Monitoring & Management',
      description: 'Real-time position limits monitoring and management across US energy exchanges with CFTC position limits tracking and automated alerts.',
      link: '/services/position-limits'
    },
    {
      icon: <FaClipboardCheck />,
      title: 'Energy Trading Reconciliation',
      description: 'Automated reconciliation of US energy trades, positions, and P&L across multiple trading systems and regulatory reporting platforms.',
      link: '/services/reconciliation'
    },
    {
      icon: <FaNetworkWired />,
      title: 'US Energy Exchange Connectivity',
      description: 'Direct connectivity to major US energy exchanges including NYMEX, ICE, and regional power markets for gas and energy trading.',
      link: '/services/exchange-connectivity'
    }
  ];

  return (
    <Section
      title="US Energy Trading Compliance Solutions"
      subtitle="Specialized position limits monitoring and regulatory compliance solutions for US gas and energy commodity trading firms"
      titleProps={{ centered: true, withLine: true, spaced: true }}
      subtitleProps={{ centered: true, muted: true }}
      offWhite
      id="services"
    >
      <ServicesGrid
        as={motion.div}
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
      >
        {services.map((service, index) => (
          <ServiceCard 
            key={index} 
            variants={item}
            whileHover={{ 
              y: -8,
              transition: { duration: 0.3 }
            }}
          >
            <ServiceIcon className="service-icon">
              {service.icon}
            </ServiceIcon>
            <ServiceTitle>{service.title}</ServiceTitle>
            <ServiceDescription>{service.description}</ServiceDescription>
            <ServiceLink 
              to={service.link}
              className="service-link"
            >
              Learn more
            </ServiceLink>
          </ServiceCard>
        ))}
      </ServicesGrid>
    </Section>
  );
};

export default Services; 